<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  call: {
    type: Object,
    default: null,
  },
});

const now = ref(Date.now());
let timer = null;

const isRinging = computed(() => props.call?.status === 'ringing');
const isInProgress = computed(
  () => props.call?.status === 'in_progress'
);

const statusLabel = computed(() => {
  if (isRinging.value) return 'Llamando';
  if (isInProgress.value) return 'En llamada';
  return '';
});

const contactLabel = computed(() => {
  return (
    props.call?.contact_name ||
    props.call?.contact_phone ||
    'Cliente'
  );
});

const elapsedSeconds = computed(() => {
  if (!props.call) return 0;

  const backendDuration = Number(props.call.duration_seconds || 0);

  if (!props.call.started_at) {
    return backendDuration;
  }

  const startedAt = new Date(props.call.started_at).getTime();

  if (Number.isNaN(startedAt)) {
    return backendDuration;
  }

  return Math.max(
    backendDuration,
    Math.floor((now.value - startedAt) / 1000)
  );
});

const formattedDuration = computed(() => {
  const total = elapsedSeconds.value;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const values = [minutes, seconds];

  if (hours > 0) {
    values.unshift(hours);
  }

  return values.map(value => String(value).padStart(2, '0')).join(':');
});

onMounted(() => {
  timer = window.setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer);
});
</script>

<template>
  <div
    v-if="call"
    class="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-small"
  >
    <span
      class="inline-flex items-center gap-1.5 font-medium"
      :class="{
        'text-n-amber-11': isRinging,
        'text-n-ruby-11': isInProgress,
      }"
    >
      <span
        class="size-2 rounded-full"
        :class="{
          'bg-n-amber-9 animate-pulse': isRinging,
          'bg-n-ruby-9 animate-pulse': isInProgress,
        }"
      />

      {{ statusLabel }}
    </span>

    <span class="text-n-slate-11">
      con {{ contactLabel }}
    </span>

    <span
      v-if="call.contact_phone"
      class="text-n-slate-10"
    >
      {{ call.contact_phone }}
    </span>

    <span class="font-medium tabular-nums text-n-slate-11">
      {{ formattedDuration }}
    </span>
  </div>
</template>
